import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  FlaskConical,
  Users,
  DollarSign,
  Clock,
  MapPin,
  Wifi,
  Play,
  Pause,
  Trash2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Calendar,
  ChevronRight,
  Eye,
  UserCheck,
  UserX,
} from 'lucide-react';
import { api } from '@/lib/api';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] } },
};

interface StudyData {
  id: string;
  title: string;
  description: string;
  status: string;
  compensation_amount: number;
  compensation_type: string;
  duration_minutes: number;
  location: string | null;
  is_remote: boolean;
  max_participants: number;
  current_participants: number;
  category: string | null;
  starts_at: string;
  ends_at: string;
  criteria: Record<string, unknown>;
  created_at: string;
}

interface Appointment {
  id: string;
  student_id: string;
  status: string;
  booked_at: string;
  slot_start_time?: string;
  slot_end_time?: string;
  student_name?: string;
  student_email?: string;
  student_reliability_score?: number;
}

interface Slot {
  id: string;
  start_time: string;
  end_time: string;
  capacity: number;
  booked_count: number;
  is_locked: boolean;
}

export default function StudyManage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [study, setStudy] = useState<StudyData | null>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let mounted = true;
    const load = async () => {
      try {
        const [studyData, appointmentsData, slotsData] = await Promise.all([
          api.getStudy(id),
          api.getStudyAppointments(id),
          api.getSlots(id),
        ]);
        if (!mounted) return;
        setStudy(studyData);
        setAppointments(appointmentsData.data ?? appointmentsData ?? []);
        setSlots(slotsData.slots ?? slotsData ?? []);
      } catch {
        toast.error('Failed to load study');
        navigate('/my-studies');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [id, navigate]);

  const handleActivate = async () => {
    if (!id) return;
    setActionLoading('activate');
    try {
      const updated = await api.activateStudy(id);
      setStudy((s) => s ? { ...s, status: 'active' } : s);
      toast.success('Study activated! Matching notifications sent.');
    } catch (err: any) {
      toast.error(err.message || 'Failed to activate');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    if (!confirm('Are you sure you want to delete this study? This cannot be undone.')) return;
    setActionLoading('delete');
    try {
      await api.deleteStudy(id);
      toast.success('Study deleted');
      navigate('/my-studies');
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete');
    } finally {
      setActionLoading(null);
    }
  };

  const handleComplete = async (appointmentId: string) => {
    setActionLoading(`complete-${appointmentId}`);
    try {
      await api.completeAppointment(appointmentId);
      setAppointments((prev) =>
        prev.map((a) => a.id === appointmentId ? { ...a, status: 'completed' } : a)
      );
      toast.success('Marked as completed');
    } catch (err: any) {
      toast.error(err.message || 'Failed to complete');
    } finally {
      setActionLoading(null);
    }
  };

  const handleNoShow = async (appointmentId: string) => {
    setActionLoading(`noshow-${appointmentId}`);
    try {
      await api.markNoShow(appointmentId);
      setAppointments((prev) =>
        prev.map((a) => a.id === appointmentId ? { ...a, status: 'no_show' } : a)
      );
      toast.success('Marked as no-show');
    } catch (err: any) {
      toast.error(err.message || 'Failed to mark no-show');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-[3px] border-gray-200 dark:border-gray-800 border-t-brand-500 rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Loading study...</p>
        </div>
      </div>
    );
  }

  if (!study) return null;

  const fillPct = (study.current_participants / Math.max(study.max_participants, 1)) * 100;
  const bookedAppointments = appointments.filter((a) => a.status === 'booked' || a.status === 'confirmed');
  const completedAppointments = appointments.filter((a) => a.status === 'completed');
  const noShowAppointments = appointments.filter((a) => a.status === 'no_show');

  return (
    <motion.div variants={fadeUp} initial="hidden" animate="visible" className="max-w-4xl mx-auto space-y-6">
      {/* Back */}
      <button
        onClick={() => navigate('/my-studies')}
        className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to my studies
      </button>

      {/* Study Header */}
      <div className="card p-6 sm:p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <StatusBadge status={study.status} />
              {study.category && <span className="badge-brand">{study.category}</span>}
            </div>
            <h1 className="text-2xl font-bold tracking-tight mb-2">{study.title}</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{study.description}</p>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
          <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50">
            <div className="flex items-center gap-1.5 mb-1">
              <DollarSign className="w-3.5 h-3.5 text-success-500" />
              <span className="text-[10px] text-gray-500 uppercase">Compensation</span>
            </div>
            <p className="text-lg font-bold">${study.compensation_amount}</p>
          </div>
          <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50">
            <div className="flex items-center gap-1.5 mb-1">
              <Clock className="w-3.5 h-3.5 text-brand-500" />
              <span className="text-[10px] text-gray-500 uppercase">Duration</span>
            </div>
            <p className="text-lg font-bold">{study.duration_minutes} min</p>
          </div>
          <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50">
            <div className="flex items-center gap-1.5 mb-1">
              <Users className="w-3.5 h-3.5 text-accent-500" />
              <span className="text-[10px] text-gray-500 uppercase">Participants</span>
            </div>
            <p className="text-lg font-bold">{study.current_participants}/{study.max_participants}</p>
            <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full mt-1 overflow-hidden">
              <div className="h-full bg-accent-500 rounded-full" style={{ width: `${fillPct}%` }} />
            </div>
          </div>
          <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50">
            <div className="flex items-center gap-1.5 mb-1">
              {study.is_remote ? <Wifi className="w-3.5 h-3.5 text-accent-500" /> : <MapPin className="w-3.5 h-3.5 text-gray-400" />}
              <span className="text-[10px] text-gray-500 uppercase">Location</span>
            </div>
            <p className="text-sm font-semibold truncate">{study.is_remote ? 'Remote' : study.location || 'On Campus'}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 mt-6 pt-6 border-t border-gray-100 dark:border-gray-800 flex-wrap">
          {study.status === 'draft' && (
            <button
              onClick={handleActivate}
              disabled={actionLoading === 'activate'}
              className="btn-primary"
            >
              {actionLoading === 'activate' ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              Activate Study
            </button>
          )}
          {(study.status === 'draft' || study.status === 'paused') && (
            <button
              onClick={handleDelete}
              disabled={actionLoading === 'delete'}
              className="btn-danger"
            >
              {actionLoading === 'delete' ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Time Slots */}
      {slots.length > 0 && (
        <div className="card p-6">
          <h2 className="section-header mb-4">Time Slots</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {slots.map((slot) => (
              <div
                key={slot.id}
                className={`p-3 rounded-xl border ${
                  slot.is_locked
                    ? 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 opacity-60'
                    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold">
                    {new Date(slot.start_time).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </span>
                  <span className="text-xs text-gray-500">{slot.booked_count}/{slot.capacity} booked</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {new Date(slot.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  {' '}&ndash;{' '}
                  {new Date(slot.end_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </p>
                <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full mt-2 overflow-hidden">
                  <div
                    className="h-full bg-brand-500 rounded-full"
                    style={{ width: `${(slot.booked_count / Math.max(slot.capacity, 1)) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Participants */}
      <div className="card p-6">
        <h2 className="section-header mb-4">
          Participants ({appointments.length})
        </h2>

        {appointments.length === 0 ? (
          <div className="text-center py-8">
            <Users className="w-10 h-10 text-gray-300 dark:text-gray-700 mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">No participants yet</p>
            {study.status === 'draft' && (
              <p className="text-xs text-gray-400 mt-1">Activate your study to start receiving bookings</p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto -mx-6">
            <table className="w-full min-w-[500px]">
              <thead>
                <tr className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-100 dark:border-gray-800">
                  <th className="px-6 py-3">Participant</th>
                  <th className="px-6 py-3">Time</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {appointments.map((apt) => (
                  <tr key={apt.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium">{apt.student_name || 'Student'}</p>
                        <p className="text-xs text-gray-500">{apt.student_email || apt.student_id.slice(0, 8)}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {apt.slot_start_time ? (
                        <div>
                          <p className="text-xs font-medium">
                            {new Date(apt.slot_start_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(apt.slot_start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                          </p>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <AppointmentBadge status={apt.status} />
                    </td>
                    <td className="px-6 py-4">
                      {(apt.status === 'booked' || apt.status === 'confirmed') && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleComplete(apt.id)}
                            disabled={actionLoading === `complete-${apt.id}`}
                            className="text-xs font-medium text-success-500 hover:text-success-600 flex items-center gap-1"
                            title="Mark completed"
                          >
                            {actionLoading === `complete-${apt.id}` ? (
                              <div className="w-3 h-3 border-2 border-success-300 border-t-success-500 rounded-full animate-spin" />
                            ) : (
                              <UserCheck className="w-3.5 h-3.5" />
                            )}
                            Complete
                          </button>
                          <button
                            onClick={() => handleNoShow(apt.id)}
                            disabled={actionLoading === `noshow-${apt.id}`}
                            className="text-xs font-medium text-danger-500 hover:text-danger-600 flex items-center gap-1"
                            title="Mark no-show"
                          >
                            {actionLoading === `noshow-${apt.id}` ? (
                              <div className="w-3 h-3 border-2 border-danger-300 border-t-danger-500 rounded-full animate-spin" />
                            ) : (
                              <UserX className="w-3.5 h-3.5" />
                            )}
                            No-Show
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </motion.div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    draft: 'badge bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400',
    active: 'badge-success',
    paused: 'badge-warning',
    completed: 'badge-brand',
    cancelled: 'badge-danger',
  };
  return <span className={styles[status] || 'badge'}>{status}</span>;
}

function AppointmentBadge({ status }: { status: string }) {
  switch (status) {
    case 'booked':
    case 'confirmed':
      return <span className="badge-brand">{status === 'confirmed' ? 'Confirmed' : 'Booked'}</span>;
    case 'completed':
      return <span className="badge-success">Completed</span>;
    case 'no_show':
      return <span className="badge-danger">No Show</span>;
    case 'cancelled':
      return <span className="badge bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">Cancelled</span>;
    default:
      return <span className="badge">{status}</span>;
  }
}
